// Components/DepartmentProtectedRoute.js
import React, { useContext, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../Components/AuthContext';
import api from '../services/api';
import { Box, CircularProgress, Alert, Typography, Button } from '@mui/material';

const DepartmentProtectedRoute = ({ children, allowedDepartments }) => {
  const { isLoggedIn, loading: authLoading } = useContext(AuthContext);
  const isAdmin = localStorage.getItem('is_admin') === 'true';
  const [userDepartment, setUserDepartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!isLoggedIn) {
        setLoading(false);
        return;
      }
      // Admins always have access
      if (isAdmin) {
        setHasAccess(true);
        setLoading(false);
        return;
      }
      try {
        const response = await api.get('users/profile/', {
          headers: { Authorization: `Token ${localStorage.getItem('Token')}` },
        });
        const department = response.data.department;
        setUserDepartment(department);
        const deptArray = Array.isArray(allowedDepartments)
          ? allowedDepartments
          : [allowedDepartments];
        const access = deptArray.some(
          (allowed) => department?.toLowerCase() === allowed?.toLowerCase()
        );
        setHasAccess(access);
      } catch (err) {
        console.error('Error fetching user profile:', err);
        setError('Failed to verify access permissions');
      } finally {
        setLoading(false);
      }
    };
    if (!authLoading) fetchUserProfile();
  }, [isLoggedIn, authLoading, isAdmin, allowedDepartments]);

  if (authLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isLoggedIn) return <Navigate to="/login" replace />;

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
        <Alert severity="error">{error}</Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>Retry</Button>
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2, p: 3 }}>
        <Alert severity="warning" sx={{ maxWidth: 500 }}>
          <Typography variant="h6" gutterBottom>Access Restricted</Typography>
          <Typography variant="body2">
            This page is only accessible to the{' '}
            <strong>
              {Array.isArray(allowedDepartments) ? allowedDepartments.join(' or ') : allowedDepartments}
            </strong>{' '}
            department.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Your current department: <strong>{userDepartment || 'Not set'}</strong>
          </Typography>
        </Alert>
        <Button variant="contained" onClick={() => window.history.back()}>Go Back</Button>
      </Box>
    );
  }

  return children;
};

export default DepartmentProtectedRoute;
